import type { Node } from "hast";

interface CustomNode extends Node {
  type: string;
  tagName?: string;
  value?: string;
  children?: CustomNode[];
  properties?: { [key: string]: any };
}

// Simple rehype plugin to process <cite> elements
// The model outputs <cite>CHUNK_ID</cite> where CHUNK_ID is a numeric data_chunk_id.
// We look up the full metadata from responseSources.
//
// `citationNumbers` (optional, chunkId → number) supplies explicit gap-aware
// citation numbers — built through mergeCitedWithUnavailable — so inline [N]
// markers keep their stable slots when cited documents become unavailable,
// matching the Sources panel and pills. Without it, numbering falls back to
// the source's array index + 1 (legacy behavior for gap-less payloads).
export function rehypeWrapCite(options: {
  // Optional: the artifact (generated-doc) call site numbers cites purely via
  // citationNumbers and passes no responseSources.
  responseSources?: any[];
  citationNumbers?: Map<number, number>;
}) {
  const { responseSources = [], citationNumbers } = options;

  // Build a lookup map: data_chunk.id -> { source, number }
  const chunkMap = new Map<number, { source: any; number: number }>();
  // Parallel map for EXTERNAL cites (#20423 A2): cite_id -> { external, number }.
  // Externals have no data_chunk, so they can't enter chunkMap; they arrive as
  // { __external, cite_id, external } entries in responseSources.
  const externalMap = new Map<number, { external: any; number: number }>();
  responseSources.forEach((source, idx) => {
    if (source?.__external && source.cite_id != null) {
      externalMap.set(source.cite_id, {
        external: source.external,
        number: citationNumbers?.get(source.cite_id) ?? idx + 1,
      });
      return;
    }
    const chunkId = source.data_chunk?.id;
    if (chunkId != null) {
      chunkMap.set(chunkId, {
        source,
        number: citationNumbers?.get(chunkId) ?? idx + 1,
      });
    }
  });

  return (tree: CustomNode) => {
    function visit(node: CustomNode) {
      const children = node.children;
      if (!children) return;

      let i = 0;
      while (i < children.length) {
        const child = children[i];

        if (child.type === "element" && child.tagName === "cite") {
          // Collect the run of adjacent <cite> siblings starting here. A
          // whitespace-only text node between two cites doesn't break the run —
          // we blank it so the markers render flush, turning `[1] [4]` into one
          // tight multi-source stack (chat-v2 spec). Word text ends the run.
          const run: CustomNode[] = [child];
          let j = i + 1;
          while (j < children.length) {
            const sib = children[j];
            if (sib.type === "element" && sib.tagName === "cite") {
              run.push(sib);
              j++;
              continue;
            }
            if (
              sib.type === "text" &&
              (sib.value ?? "").trim() === "" &&
              j + 1 < children.length &&
              children[j + 1].type === "element" &&
              children[j + 1].tagName === "cite"
            ) {
              sib.value = ""; // collapse inter-cite whitespace
              j++;
              continue;
            }
            break;
          }

          // Process each cite first; a junk body — neither a chunk id nor
          // citation JSON (e.g. the model inventing <cite>1-74</cite> from
          // line numbers, dialogue 1873) — is dropped from the tree entirely,
          // mirroring the backend strip (_NON_NUMERIC_CITE in
          // mre/utils/chat.py). Citation chrome for a non-citation is worse
          // than nothing, and messages persisted before the backend strip
          // still carry these. Group positions are assigned AFTER the drop so
          // survivors stack correctly.
          const recognized: CustomNode[] = [];
          for (const citeNode of run) {
            const citationText = extractTextContent(citeNode).trim();
            if (
              processCiteNode(
                citeNode,
                citationText,
                chunkMap,
                externalMap,
                citationNumbers,
              )
            ) {
              recognized.push(citeNode);
            } else {
              children.splice(children.indexOf(citeNode), 1);
              j--;
            }
          }

          recognized.forEach((citeNode, pos) => {
            citeNode.properties!.citeGroupPosition =
              recognized.length === 1
                ? "single"
                : pos === 0
                  ? "start"
                  : pos === recognized.length - 1
                    ? "end"
                    : "middle";
          });

          i = j;
          continue;
        }

        // Recursively process non-cite child nodes
        visit(child);
        i++;
      }
    }

    visit(tree);
  };
}

/** Where a citation marker sits within a run of adjacent cites (for stacking). */
export type CiteGroupPosition = "single" | "start" | "middle" | "end";

/**
 * Attach citation properties to a cite element. Returns false when the body
 * is not a recognizable citation (not a full-numeric chunk id, not citation
 * JSON) — the caller removes the node in that case. Group position is
 * assigned by the caller after junk removal.
 */
function processCiteNode(
  node: CustomNode,
  text: string,
  chunkMap: Map<number, { source: any; number: number }>,
  externalMap: Map<number, { external: any; number: number }>,
  citationNumbers?: Map<number, number>,
): boolean {
  let citationNumber: number | null = null;
  let hasSource = false;
  let sourceData: any = null;
  let citationData: any = null;

  // Try parsing as a plain numeric chunk ID first (new format: <cite>12345</cite>)
  const chunkId = parseInt(text, 10);
  if (!isNaN(chunkId) && String(chunkId) === text) {
    const match = chunkMap.get(chunkId);
    const externalMatch = externalMap.get(chunkId);
    if (match) {
      hasSource = true;
      citationNumber = match.number;
      sourceData = buildSourceData(chunkId, match.source);
      citationData = { chunk_id: chunkId };
    } else if (externalMatch) {
      // External (ADAMS/OSTI/web) cite — no chunk row; render from the external
      // record (title/url/snippet), fully inlined, no lazy fetch (#20423 A2).
      hasSource = true;
      citationNumber = externalMatch.number;
      sourceData = buildExternalSourceData(chunkId, externalMatch.external);
      citationData = { chunk_id: chunkId };
    } else {
      // Valid chunk ID but not in current response sources — needs lazy fetch.
      // A cited-but-unavailable document still owns its stable slot in
      // `citationNumbers` (from the payload's citation_index), so the marker
      // keeps the number the Sources panel shows for it (D4); a chunk with no
      // slot anywhere stays unnumbered.
      citationNumber = citationNumbers?.get(chunkId) ?? null;
      sourceData = {
        id: chunkId,
        data: {
          file_name: "document",
          extracted_metadata: { title: "Document" },
        },
        chunk_metadata: { page_number: undefined },
        needsFetch: true,
      };
      citationData = { chunk_id: chunkId };
    }
  } else {
    // Legacy fallback: try parsing JSON (old format: <cite>{"chunk_id": 123, ...}</cite>)
    const startIdx = text.indexOf("{");
    const endIdx = text.lastIndexOf("}");

    if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
      const jsonSlice = text.slice(startIdx, endIdx + 1);
      try {
        const metadata = JSON.parse(jsonSlice);

        if (metadata && metadata.error) {
          sourceData = {
            id: metadata.chunk_number || metadata.chunk_id || 0,
            data: {
              file_name: "document",
              extracted_metadata: { title: "Source unavailable" },
            },
            chunk_metadata: { page_number: undefined },
            hasError: true,
          };
        } else if (metadata && metadata.chunk_id) {
          citationData = metadata;
          const legacyChunkId = metadata.chunk_id;
          const match = chunkMap.get(legacyChunkId);
          const externalMatch = externalMap.get(
            metadata.cite_id ?? legacyChunkId,
          );

          if (match) {
            hasSource = true;
            citationNumber = match.number;
            sourceData = buildSourceData(legacyChunkId, match.source);
          } else if (externalMatch) {
            // External cite in resolved-JSON form — process_citations rewrites
            // the model's numeric cite to {chunk_id, cite_id, is_external}
            // before the frontend sees it, so externals must resolve through
            // externalMap here too, not only in the numeric branch above.
            hasSource = true;
            citationNumber = externalMatch.number;
            sourceData = buildExternalSourceData(
              legacyChunkId,
              externalMatch.external,
            );
          } else if (metadata.is_external) {
            // External cite with no record yet (provenance still loading or
            // projection dropped it). Synthetic external ids have no chunk
            // row, so the chunks/by-ids lazy fetch can never resolve them —
            // render a sourceless external placeholder instead of needsFetch.
            citationNumber =
              citationNumbers?.get(metadata.cite_id ?? legacyChunkId) ?? null;
            sourceData = buildExternalSourceData(legacyChunkId, {
              title: metadata.title,
            });
          } else {
            const displayTitle =
              metadata.title || metadata.filename || "Document";
            sourceData = {
              id: legacyChunkId,
              data: {
                file_name: metadata.filename || "document",
                extracted_metadata: { title: displayTitle },
              },
              chunk_metadata: {
                page_number: metadata.page_number,
                start_row: metadata.start_row,
                end_row: metadata.end_row,
                sheet_name: metadata.sheet_name,
              },
              needsFetch: true,
            };
          }
        }
      } catch {
        // Malformed JSON — ignore
      }
    }
  }

  // Nothing recognized the body: not numeric, not (valid) citation JSON.
  if (!hasSource && sourceData == null && citationData == null) {
    return false;
  }

  // Set properties on the cite element for CitationLink
  if (!node.properties) {
    node.properties = {};
  }
  // An UNMATCHED cite keeps citationNumber null and renders unnumbered —
  // defaulting to 1 mislabeled every unresolved marker as source [1]. The
  // lazy-fetch path (needsFetch) resolves legacy citations' metadata; their
  // number fills in once the fetched chunk joins the numbered source list.
  node.properties.citationNumber = citationNumber;
  node.properties.hasSource = hasSource;
  node.properties.sourceData = sourceData;
  node.properties.citationData = citationData;
  return true;
}

/** Build normalized sourceData from a responseSources entry */
function buildSourceData(chunkId: number, source: any) {
  const sourceRecord = source.source_data_record?.data || {};
  const extractedMetadata = sourceRecord.extracted_metadata || {};
  const cm = source.data_chunk?.chunk_metadata || {};

  // Tabular datasets store one SourceDataRecord per row, so source_uid is the
  // row's identifier (the configured id-column value). Such rows carry no
  // title/file_name, which is why they'd otherwise render as a bare
  // "Document"; surface the identifier instead.
  const sourceUid = source.source_data_record?.source_uid;

  return {
    id: chunkId,
    source_uid: sourceUid,
    sdr_id: source.source_data_record?.id,
    dataset_id: source.dataset?.id ?? source.source_data_record?.dataset_fk,
    // Project-folder file chunks: scoped by project_folder_fk instead of a
    // dataset. Lets the AA Projects presentation resolve the citation into
    // its file rail (spec §5.10).
    folder_id: source.source_data_record?.project_folder_fk,
    dataset_name: source.dataset?.name,
    dataset_type_id:
      source.dataset?.dataset_type_id ?? source.dataset?.dataset_type?.id,
    data: {
      file_name: sourceRecord.file_name || "document",
      extracted_metadata: {
        title:
          extractedMetadata.title ||
          sourceRecord.file_name ||
          sourceUid ||
          "Document",
      },
    },
    chunk_metadata: {
      page_number: cm.page_number,
      // Tabular chunks (CSV/XLSX via HTMLTableChunker) carry row ranges so
      // CitationLink can render "(rows N–M, SheetX)" instead of the synthetic
      // "p. N" derived from chunk_index + 1.
      start_row: cm.start_row,
      end_row: cm.end_row,
      sheet_name: cm.sheet_name,
    },
    rawData: sourceRecord,
    extractedMetadata: extractedMetadata,
    chunkText: source.data_chunk?.chunk_data?.data || "",
    // Top-level provenance fields from the backend serializer (#20423
    // Story 2) — the hover tooltip reads these off sourceData to render the
    // classification chip and the Revision / Via / Retrieved / Query rows.
    classification: source.classification,
    retrieved_by_agent: source.retrieved_by_agent,
    primary_retrieval: source.primary_retrieval,
    revision: source.revision,
    retrieved_at: source.retrieved_at,
    retrieval_query: source.retrieval_query,
  };
}

/**
 * Build normalized sourceData for a cited EXTERNAL source (#20423 A2). Externals
 * have no chunk/SDR/dataset row — everything the tooltip and click need comes
 * off the ExternalSource record (title/url/provider/snippet). `isExternal` +
 * `url` drive CitationLink's link-open branch and provider chip; `chunkText`
 * reuses the same snippet slot the chunk renderer reads.
 */
function buildExternalSourceData(citeId: number, external: any) {
  const ext = external || {};
  const provider = ext.source_type || null;
  const title = ext.title || ext.identifier || "External source";
  return {
    id: citeId,
    isExternal: true,
    url: ext.url || null,
    source_type: provider,
    identifier: ext.identifier ?? null,
    published_date: ext.published_date ?? null,
    data: {
      file_name: title,
      extracted_metadata: { title },
    },
    chunk_metadata: { page_number: undefined },
    chunkText: ext.snippet || "",
    // Provenance fields the hover tooltip reads; externals are always the
    // external_library classification (backend serialize_external, G1).
    classification: ext.classification || "external_library",
    retrieved_by_agent: ext.retrieved_by_agent ?? null,
    retrieved_at: ext.retrieved_at ?? null,
  };
}

function extractTextContent(node: CustomNode): string {
  if (node.type === "text") {
    let text = node.value || "";
    // Unescape characters that were escaped to survive markdown parsing
    // (see AAAssistantText.tsx processedContent for the escaping logic)
    text = text.replace(/\\\\"/g, '\\"');
    text = text.replace(/\\_/g, "_");
    text = text.replace(/\\\*/g, "*");
    text = text.replace(/\\`/g, "`");
    return text;
  }

  if (node.children) {
    return node.children.map(extractTextContent).join("");
  }

  return "";
}
